// 资源包：本机下载、暂停续传、完成后才对编辑器/试玩生效。
// 数据只存在这台设备的浏览器里（IndexedDB），不需要登录，也不绑定账号。

const DB_NAME = 'infworld-resource-packs';
const DB_VERSION = 1;
const STORE_FILES = 'files';
const STORE_META = 'meta';

const brickFile = (q, kind, ext) => `/textures/brick/red_brick_${q}/textures/red_brick_${kind}_${q}.${ext}`;
const TEXTURE_FILES = [];
['1k', '2k', '4k'].forEach(q => {
  TEXTURE_FILES.push(brickFile(q, 'diff', 'jpg'));
  TEXTURE_FILES.push(brickFile(q, 'disp', 'png'));
  TEXTURE_FILES.push(brickFile(q, 'nor_gl', 'png'));
});

const QUALITY_PACKS = [
  { quality: '1k', suffix: '流畅', description: '1K 原始贴图，体积最小，下完这一档即可在本机生效。' },
  { quality: '2k', suffix: '标准', description: '2K 原始贴图，按需单独下载，不影响已下载的其他画质。' },
  { quality: '4k', suffix: '高清', description: '4K 原始贴图，文件较大，只在需要时下载。' },
];

export const RESOURCE_PACKS = QUALITY_PACKS.map(item => ({
  id: `material-textures-${item.quality}`,
  legacyId: 'material-textures',
  quality: item.quality,
  name: `材质纹理 · ${item.suffix} ${item.quality.toUpperCase()}`,
  description: item.description,
  cover: '#4fc3f7',
  version: 1,
  files: TEXTURE_FILES.filter(url => url.includes(`red_brick_${item.quality}/`)),
}));

export function formatBytes(n) {
  const num = Number(n) || 0;
  if (num <= 0) return '0 B';
  if (num < 1024) return `${num} B`;
  if (num < 1024 * 1024) return `${(num / 1024).toFixed(1)} KB`;
  if (num < 1024 * 1024 * 1024) return `${(num / 1024 / 1024).toFixed(1)} MB`;
  return `${(num / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

// ---------- IndexedDB ----------
let dbPromise = null;
function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_FILES)) db.createObjectStore(STORE_FILES);
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

async function idbGet(store, key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const req = db.transaction(store, 'readonly').objectStore(store).get(key);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

async function idbPut(store, key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, 'readwrite');
    tx.objectStore(store).put(value, key);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

// ---------- 下载状态 ----------
const states = {};
const listeners = new Set();
const readyPacks = new Set();
const parts = {};       // url -> { chunks, received }（暂停时保留在内存，用于续传）
const controllers = {}; // packId -> AbortController
const running = {};     // packId -> boolean
const speedTrack = {};  // packId -> 多文件并发下载的合计速度采样
const autoResume = {};  // packId -> 是否需要在刷新后自动续下（手动暂停则为 false）
let lastEmit = 0;

function blankState(pack) {
  return {
    status: 'idle', // idle | downloading | paused | done | error
    total: 0,
    downloaded: 0,
    percent: 0,
    speed: 0,
    error: '',
    files: pack.files.map(url => ({ url, size: 0, received: 0, done: false })),
  };
}

function recalc(packId) {
  const st = states[packId];
  if (!st) return;
  let total = 0;
  let downloaded = 0;
  st.files.forEach(f => {
    total += f.size || 0;
    downloaded += f.done ? (f.size || f.received || 0) : (f.received || 0);
  });
  st.total = total;
  st.downloaded = downloaded;
  st.percent = total > 0 ? Math.min(100, (downloaded / total) * 100) : 0;
}

function emit(force) {
  const now = Date.now();
  if (!force && now - lastEmit < 200) return;
  lastEmit = now;
  listeners.forEach(fn => {
    try { fn(); } catch (e) { /* 忽略订阅方错误 */ }
  });
}

export function subscribeResourcePacks(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function getResourcePackState(packId) {
  return states[packId] || blankState(RESOURCE_PACKS.find(p => p.id === packId) || RESOURCE_PACKS[0]);
}

async function saveProgress(packId) {
  const pack = RESOURCE_PACKS.find(p => p.id === packId);
  const st = states[packId];
  if (!pack || !st) return;
  try {
    await idbPut(STORE_META, packId, {
      version: pack.version,
      done: st.status === 'done',
      active: !!autoResume[packId] && st.status !== 'done',
      files: st.files.map(f => ({ url: f.url, size: f.size, done: f.done })),
      updatedAt: Date.now(),
    });
  } catch (e) { /* 存储失败不阻断下载 */ }
}

// 取文件大小（只取一次，之后记住）
async function ensureSizes(packId) {
  const st = states[packId];
  if (!st) return;
  const pending = st.files.filter(f => !f.size);
  if (!pending.length) { recalc(packId); emit(true); return; }
  await Promise.all(pending.map(async f => {
    try {
      const resp = await fetch(f.url, { method: 'HEAD' });
      const len = Number(resp.headers.get('Content-Length')) || 0;
      if (resp.ok && len) f.size = len;
    } catch (e) { /* 网络不可用时保持未知 */ }
  }));
  recalc(packId);
  emit(true);
}

let initPromise = null;
export function initResourcePacks() {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    let legacyMeta = null;
    let migratedLegacy = false;
    try { legacyMeta = await idbGet(STORE_META, 'material-textures'); } catch (e) { /* 无旧版数据 */ }

    for (const pack of RESOURCE_PACKS) {
      states[pack.id] = blankState(pack);
      try {
        let meta = await idbGet(STORE_META, pack.id);
        let migrated = false;
        if (!meta && legacyMeta && legacyMeta.version === pack.version && Array.isArray(legacyMeta.files)) {
          meta = { ...legacyMeta, files: legacyMeta.files.filter(saved => pack.files.includes(saved.url)) };
          migrated = true;
          migratedLegacy = true;
        }
        if (meta && meta.version === pack.version && Array.isArray(meta.files)) {
          meta.files.forEach(saved => {
            const f = states[pack.id].files.find(x => x.url === saved.url);
            if (f) { f.size = saved.size || 0; f.done = !!saved.done; f.received = f.done ? f.size : 0; }
          });
          const allDone = states[pack.id].files.every(f => f.done);
          states[pack.id].status = allDone ? 'done' : 'paused';
          autoResume[pack.id] = !!meta.active && !allDone && (!migrated || pack.quality === '1k');
          if (allDone) readyPacks.add(pack.id);
          if (migrated) await saveProgress(pack.id);
        }
      } catch (e) { /* 无本地数据 */ }
      recalc(pack.id);
    }
    // 三档都已写入各自进度后，清掉旧索引，避免删除资源后又被旧记录恢复。
    if (migratedLegacy) {
      try { await idbPut(STORE_META, 'material-textures', null); } catch (e) { /* 不影响新索引 */ }
    }
    emit(true);
    RESOURCE_PACKS.forEach(p => { ensureSizes(p.id); });
  })();
  // 上次离开时仍在下载的包，自动接着下；用户手动暂停过的不会自动开始。
  initPromise.then(() => {
    RESOURCE_PACKS.forEach(p => {
      if (autoResume[p.id] && states[p.id] && states[p.id].status !== 'done') {
        startResourcePackDownload(p.id);
      }
    });
  }).catch(() => {});
  return initPromise;
}

function parseContentRangeTotal(value) {
  if (!value) return 0;
  const m = /\/(\d+)\s*$/.exec(value);
  return m ? Number(m[1]) : 0;
}

function updateDownloadProgress(packId) {
  const st = states[packId];
  recalc(packId);
  const now = Date.now();
  const sample = speedTrack[packId];
  if (sample && now - sample.time >= 500) {
    st.speed = Math.max(0, ((st.downloaded - sample.bytes) * 1000) / (now - sample.time));
    sample.time = now;
    sample.bytes = st.downloaded;
  }
  emit(false);
}

async function downloadFile(pack, f, signal) {
  const previous = parts[f.url];
  const start = previous ? previous.received : 0;
  const headers = {};
  if (start > 0) headers.Range = `bytes=${start}-`;

  const resp = await fetch(f.url, { headers, signal });
  if (!resp.ok && resp.status !== 206) throw new Error(`下载失败（HTTP ${resp.status}）`);
  if (!resp.body || !resp.body.getReader) throw new Error('当前浏览器不支持分段下载');

  const resume = resp.status === 206 && start > 0;
  let chunks = resume ? previous.chunks : [];
  let received = resume ? previous.received : 0;

  const len = Number(resp.headers.get('Content-Length')) || 0;
  const rangeTotal = parseContentRangeTotal(resp.headers.get('Content-Range'));
  const size = rangeTotal || (resume ? received + len : len);
  if (size) f.size = size;

  const reader = resp.body.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.byteLength;
    f.received = received;
    parts[f.url] = { chunks, received };
    updateDownloadProgress(pack.id);
  }

  const blob = new Blob(chunks, { type: resp.headers.get('Content-Type') || 'application/octet-stream' });
  await idbPut(STORE_FILES, f.url, blob);
  f.done = true;
  f.received = f.size || received;
  delete parts[f.url];
  recalc(pack.id);
  await saveProgress(pack.id);
  emit(true);
}

export async function startResourcePackDownload(packId) {
  await initResourcePacks();
  const pack = RESOURCE_PACKS.find(p => p.id === packId);
  const st = states[packId];
  if (!pack || !st || running[packId] || st.status === 'done') return;

  st.status = 'downloading';
  st.error = '';
  st.speed = 0;
  autoResume[packId] = true;
  saveProgress(packId);
  running[packId] = true;
  speedTrack[packId] = { time: Date.now(), bytes: st.downloaded };
  const controller = new AbortController();
  controllers[packId] = controller;
  emit(true);

  try {
    // 每档最多三个文件并行，不再让法线图、颜色图和凹凸图依次排队。
    const pending = st.files.filter(f => !f.done);
    let cursor = 0;
    const worker = async () => {
      for (;;) {
        const index = cursor++;
        if (index >= pending.length) return;
        await downloadFile(pack, pending[index], controller.signal);
      }
    };
    const results = await Promise.allSettled(
      Array.from({ length: Math.min(3, pending.length) }, () => worker())
    );
    const failed = results.find(result => result.status === 'rejected');
    if (failed) throw failed.reason;

    st.status = 'done';
    st.speed = 0;
    autoResume[packId] = false;
    readyPacks.add(packId);
    recalc(packId);
    await saveProgress(packId);
    emit(true);
  } catch (e) {
    st.speed = 0;
    if (e && e.name === 'AbortError') {
      st.status = 'paused';
    } else {
      st.status = 'error';
      st.error = (e && e.message) || '下载失败';
    }
    recalc(packId);
    await saveProgress(packId);
    emit(true);
  } finally {
    running[packId] = false;
    delete controllers[packId];
    delete speedTrack[packId];
  }
}

export function pauseResourcePackDownload(packId) {
  autoResume[packId] = false;
  const controller = controllers[packId];
  if (controller) controller.abort();
  saveProgress(packId);
}

export async function removeResourcePack(packId) {
  await initResourcePacks();
  pauseResourcePackDownload(packId);
  const pack = RESOURCE_PACKS.find(p => p.id === packId);
  if (!pack) return;
  const db = await openDB();
  await new Promise(resolve => {
    const tx = db.transaction([STORE_FILES, STORE_META], 'readwrite');
    const files = tx.objectStore(STORE_FILES);
    pack.files.forEach(url => files.delete(url));
    tx.objectStore(STORE_META).delete(packId);
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => resolve(false);
  });
  readyPacks.delete(packId);
  autoResume[packId] = false;
  states[packId] = blankState(pack);
  recalc(packId);
  emit(true);
  ensureSizes(packId);
}

// 只有整个包下载完成，才允许材质加载器读取本机文件。
export async function getLocalTextureBlob(url) {
  if (!url || url.indexOf('/textures/') === -1) return null;
  const pack = RESOURCE_PACKS.find(p => p.files.indexOf(url) !== -1);
  if (!pack) return null;
  await initResourcePacks();
  if (!readyPacks.has(pack.id)) return null;
  try {
    return await idbGet(STORE_FILES, url);
  } catch (e) {
    return null;
  }
}
